import React, { useState } from 'react';
import { SectionsData } from '../types/analytics.types';
import './SectionPerformanceTable.css';

interface SectionPerformanceTableProps {
  data: SectionsData;
  loading?: boolean;
}

type SortField = 'section' | 'actions' | 'users' | 'success' | 'completion';
type SortDirection = 'asc' | 'desc';

export const SectionPerformanceTable: React.FC<SectionPerformanceTableProps> = ({ data, loading }) => {
  const [sortField, setSortField] = useState<SortField>('actions');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  if (loading) {
    return (
      <div className="section-performance">
        <h4>Section Performance</h4>
        <div className="loading-skeleton" style={{ height: '400px' }}></div>
      </div>
    );
  }

  if (!data || !data.section_metrics || data.section_metrics.length === 0) {
    return (
      <div className="section-performance">
        <h4>Section Performance</h4>
        <p className="no-data">No section data available</p>
      </div>
    );
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter and sort data
  const filteredData = data.section_metrics.filter(section =>
    section.section.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedData = [...filteredData].sort((a, b) => {
    let aVal: number | string;
    let bVal: number | string;

    switch (sortField) {
      case 'section':
        aVal = a.section;
        bVal = b.section;
        break;
      case 'actions':
        aVal = a.total_actions;
        bVal = b.total_actions;
        break;
      case 'users':
        aVal = a.unique_users;
        bVal = b.unique_users;
        break;
      case 'success':
        aVal = a.success_rate;
        bVal = b.success_rate;
        break;
      case 'completion':
        aVal = a.completion_rate;
        bVal = b.completion_rate;
        break;
      default:
        aVal = a.total_actions;
        bVal = b.total_actions;
    }

    if (typeof aVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal as string)
        : (bVal as string).localeCompare(aVal);
    }

    return sortDirection === 'asc' ? aVal - (bVal as number) : (bVal as number) - aVal;
  });

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '⇅';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getSuccessColor = (rate: number) => {
    if (rate >= 80) return 'success-high';
    if (rate >= 60) return 'success-medium';
    return 'success-low';
  };

  const getCompletionColor = (rate: number) => {
    if (rate >= 70) return 'completion-high';
    if (rate >= 40) return 'completion-medium';
    return 'completion-low';
  };

  return (
    <div className="section-performance">
      <div className="performance-header">
        <h4>Section Performance Analysis</h4>
        <div className="search-box">
          <input
            type="text"
            placeholder="Search sections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="table-container">
        <table className="performance-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('section')} className="sortable">
                Section {getSortIcon('section')}
              </th>
              <th onClick={() => handleSort('actions')} className="sortable">
                Actions {getSortIcon('actions')}
              </th>
              <th onClick={() => handleSort('users')} className="sortable">
                Users {getSortIcon('users')}
              </th>
              <th onClick={() => handleSort('success')} className="sortable">
                Success Rate {getSortIcon('success')}
              </th>
              <th onClick={() => handleSort('completion')} className="sortable">
                Completion {getSortIcon('completion')}
              </th>
              <th>Avg Response</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((section, index) => (
              <tr key={index}>
                <td className="section-name" title={section.section}>
                  {section.section.length > 30
                    ? section.section.substring(0, 30) + '...'
                    : section.section}
                </td>
                <td className="number-cell">{section.total_actions.toLocaleString()}</td>
                <td className="number-cell">{section.unique_users.toLocaleString()}</td>
                <td className="number-cell">
                  <span className={`badge ${getSuccessColor(section.success_rate)}`}>
                    {section.success_rate.toFixed(1)}%
                  </span>
                </td>
                <td className="number-cell">
                  <span className={`badge ${getCompletionColor(section.completion_rate)}`}>
                    {section.completion_rate.toFixed(1)}%
                  </span>
                  <span className="completion-detail">
                    ({section.completed}/{section.total_enrollments})
                  </span>
                </td>
                <td className="number-cell">
                  {section.avg_response_time_ms > 0
                    ? `${(section.avg_response_time_ms / 1000).toFixed(1)}s`
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-summary">
        <span>Showing {sortedData.length} of {data.total_sections} sections</span>
      </div>
    </div>
  );
};

export default SectionPerformanceTable;
